import React, { useState } from 'react';
import { FiAlertCircle, FiSliders, FiArrowRight, FiPlus, FiUser, FiX } from 'react-icons/fi';
import MegaphoneIcon from '../../MegaphoneIcon';
import Toggle from '../../Toggle';
import { initials } from '../../Header';
import { toast } from '../../Toast';
import { PEOPLE, useClient } from '../clientSettingsData';
import { RuleSet } from './announcementData';
import { SectionProps } from './AnnouncementSettings';
import StandardFilterModal from './StandardFilterModal';

const RULES: { key: keyof RuleSet; label: string }[] = [
    { key: 'email', label: 'Email subscribers automatically' },
    { key: 'confirm', label: 'Send confirmation to approvers' },
];

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const Rules: React.FC<SectionProps> = ({ form, setForm }) => {
    const { name } = useClient();
    const [adding, setAdding] = useState(false);
    const [word, setWord] = useState('');
    const [approver, setApprover] = useState('');
    const [standard, setStandard] = useState(false);

    const setRule = (group: 'priceSensitive' | 'other', key: keyof RuleSet, value: boolean) =>
        setForm(f => ({ ...f, [group]: { ...f[group], [key]: value } }));

    const has = (w: string) => form.filters.some(x => x.toLowerCase() === w.trim().toLowerCase());

    const addFilters = (words: string[]) => setForm(f => ({ ...f, filters: [...f.filters, ...words.filter(w => !f.filters.some(x => x.toLowerCase() === w.toLowerCase()))] }));

    const addWord = () => {
        const w = word.trim();
        if (w && !has(w)) addFilters([w]);
        setWord('');
        setAdding(false);
    };

    // a team member by name or email, or any email address
    const addApprover = () => {
        const q = approver.trim();
        if (!q) return;
        const person = PEOPLE(name).find(p => p.name.toLowerCase() === q.toLowerCase() || p.email.toLowerCase() === q.toLowerCase());
        const next = person ? { name: person.name, email: person.email } : EMAIL.test(q) ? { email: q } : null;
        if (!next) {
            toast.error('Type a team member’s name or an email address');
            return;
        }
        if (!form.approvers.some(a => a.email.toLowerCase() === next.email.toLowerCase())) setForm(f => ({ ...f, approvers: [...f.approvers, next] }));
        setApprover('');
    };

    const group = (key: 'priceSensitive' | 'other', title: string) =>
        <div className={`rule-group ${key}`}>
            <div className="head"><span className="icon-tile"><FiAlertCircle /></span><strong>{title}</strong></div>
            {RULES.map(r => <div key={r.key} className="rule">
                <span className="label">{r.label}</span>
                <span className={`yes-no${form[key][r.key] ? ' on' : ''}`}>{form[key][r.key] ? 'Yes' : 'No'}</span>
                <Toggle label={`${title}: ${r.label}`} checked={form[key][r.key]} onChange={v => setRule(key, r.key, v)} />
            </div>)}
        </div>;

    const standardLink = (cls: string) => <button type="button" className={`link ${cls}`} onClick={() => setStandard(true)}>Insert Standard Filter <FiArrowRight /></button>;

    return <>
        <section className="card">
            <div className="section-title">
                <span className="icon-tile"><MegaphoneIcon /></span>
                <div className="text">
                    <h2>When an Announcement Arrives</h2>
                    <div className="hint">Price‑sensitive announcements are emailed automatically. Others wait for an Approver.</div>
                </div>
            </div>
            <div className="rule-groups">
                {group('priceSensitive', 'Price sensitive')}
                {group('other', 'Not price sensitive')}
            </div>
        </section>

        <section className="card">
            <div className="section-title">
                <span className="icon-tile"><FiSliders /></span>
                <div className="text">
                    <h2>Don't Auto‑Send These</h2>
                    <div className="hint">Announcements whose headline matches a filter are never emailed automatically</div>
                </div>
                {standardLink('in-title')}
            </div>
            <div className="chips-wrap">
                {form.filters.map(f => <span key={f} className="chip">
                    {f}
                    <button type="button" aria-label={`Remove ${f}`} onClick={() => setForm(x => ({ ...x, filters: x.filters.filter(y => y !== f) }))}><FiX /></button>
                </span>)}
                {adding
                    ? <input className="chip-input" autoFocus value={word} placeholder="Word or phrase" onChange={e => setWord(e.target.value)}
                        onBlur={addWord} onKeyDown={e => { if (e.key === 'Enter') addWord(); if (e.key === 'Escape') { setWord(''); setAdding(false); } }} />
                    : <button type="button" className="chip add" onClick={() => setAdding(true)}><FiPlus /> Add a word or phrase</button>}
            </div>
            {standardLink('at-bottom')}
        </section>

        <section className="card">
            <div className="section-title"><span className="icon-tile"><FiUser /></span><h2>Who Approves and Gets Confirmations</h2></div>
            <div className="tag-input">
                {form.approvers.length > 0 && <div className="tags">
                    {form.approvers.map(a => <span key={a.email} className="tag">
                        <span className="av">{initials(a.name ?? a.email)}</span>
                        <span className="who">{a.name ? `${a.name} · ${a.email}` : a.email}</span>
                        <button type="button" aria-label={`Remove ${a.email}`} onClick={() => setForm(f => ({ ...f, approvers: f.approvers.filter(x => x.email !== a.email) }))}><FiX /></button>
                    </span>)}
                </div>}
                <input list="approver-people" value={approver} placeholder="Add a person or email…" onChange={e => setApprover(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addApprover(); } }} />
                <datalist id="approver-people">{PEOPLE(name).map(p => <option key={p.id} value={p.name}>{p.email}</option>)}</datalist>
            </div>
            <div className="setting-row">
                <div className="text">
                    <div className="title">Remind approvers</div>
                    <div className="hint">If nothing has been approved after 2 hours on a trading day, send an SMS reminder</div>
                </div>
                <Toggle label="Remind approvers" checked={form.remind} onChange={v => setForm(f => ({ ...f, remind: v }))} />
            </div>
        </section>

        {standard && <StandardFilterModal existing={form.filters} onClose={() => setStandard(false)}
            onAdd={words => { addFilters(words); setStandard(false); }} />}
    </>;
};

export default Rules;
